"use client";

import React, { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/providers/AuthProvider";
import { useCreatePageStore } from "@/stores/createPageStore";
import { logger } from "@/utils/logger";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { redirectToLoginWithDestination } from "@/hooks/useRedirectToLogin";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { prepareSavePointPayload } from "@/hooks/create-page/utils/formStateHelpers";
import { needsAnonymousWorkHandoff } from "@/lib/auth/accountStatus";
import {
  CREATE_SIGN_IN_HANDOFF_ERROR,
  prepareCreateSignInHandoff,
} from "@/utils/createSignInHandoff";

interface LoginRequiredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
}

/**
 * Login-required dialog for anonymous credit-consuming actions. "Log In"
 * saves the current create-page state and redirects to login so the user
 * can resume where they left off.
 */
export default function LoginRequiredDialog({
  open,
  onOpenChange,
  title = "Save your dress to continue",
  description = "Create an account or sign in. Your dress and chat will stay here.",
}: LoginRequiredDialogProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setHandoffError(null);
    onOpenChange(nextOpen);
  };
  const continueToAuth = (mode: "sign-in" | "sign-up") => {
    try {
      setHandoffError(null);
      const currentState = useCreatePageStore.getState();
      const currentUrl = searchParams.toString()
        ? `${pathname}?${searchParams.toString()}`
        : pathname;
      let returnPath = currentUrl;

      if (!user || needsAnonymousWorkHandoff(user)) {
        const handoff = prepareCreateSignInHandoff({
          state: currentState,
          currentHref: window.location.href,
          sourceUserId: user?.id,
        });
        if (!handoff) {
          setHandoffError(CREATE_SIGN_IN_HANDOFF_ERROR);
          return;
        }
        returnPath = handoff.returnPath;
      } else {
        // A truly signed-out visitor has no source Auth row to adopt. Keep the
        // canonical snapshot path for that case; initial null -> account auth
        // hydration is not an identity-owner transition.
        FormPersistenceService.saveState({
          data: prepareSavePointPayload(currentState, 1),
          source: "auth_redirect",
        });
      }

      handleOpenChange(false);
      redirectToLoginWithDestination(router, returnPath, mode);
    } catch (error) {
      logger.error("Failed to save state and redirect to login", {
        component: "CreateAgentChat",
        metadata: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      setHandoffError(CREATE_SIGN_IN_HANDOFF_ERROR);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent title={title}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          {handoffError ? (
            <p role="alert" className="text-sm text-destructive">
              {handoffError}
            </p>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => continueToAuth("sign-in")}>
            Sign in
          </Button>
          <Button onClick={() => continueToAuth("sign-up")}>
            Create account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
